import DesignTokens from '@tableau/design-tokens';
import { SiteUrls } from '@tableau/shared-urls';
import { TabStyles } from '@tableau/widgets';
import { HeaderColumn } from '@tableau/widgets-datagrid';
import React from 'react';
import ReactDOM from 'react-dom';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { CreateExplorerEvent } from '~commonInfra/telemetryEvents/TelemetryEventCreator';
import { ExplorerSortEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { imgUrl } from '~commonInfra/utils/ImgUrl';
import { showDMAwareness } from '~src/commonInfra/components/DMAwareness/DmAwarenessUtils';
import { dqwPreviewDialog } from '~src/commonInfra/components/DMAwareness/DQWPreview';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { applyColor }from '~src/style/ColorService';

import { ContentSortOptions } from '../../models/ContentSortOptions';
import { dataKeyToCrassFieldMap } from '../../models/MixedContentInCrassSortOptions';

// These are (mostly) a direct copy of common/DataGridCells
// Here they just use the classes from widgets-datagrid

// Borrowed from datagrid
// tslint:disable-next-line:no-any
export function measureWidth(componentOrElement: React.Component<any, any> | Element): number {
  const domNode: Element = ReactDOM.findDOMNode(componentOrElement) as Element;
  const rect = domNode.getBoundingClientRect();
  return Math.ceil(rect.right - rect.left);
}

export function getPrimaryOrderClause(currentSort: ReadonlyArray<IOrderClause>): Readonly<IOrderClause> {
  // Tolerate not just an empty array but a 1-element array whose sole element is undefined
  // (the shape the Groups grids pass into SortableHeader.Formatter from a nullable prop) --
  // returning the default here lets callers pass the raw clause without their own null-guard.
  const first = currentSort && currentSort[0];
  return first ? first : { field: undefined, ascending: false };
}

export function doesOrderClauseSupportDirections(orderClause: Readonly<IOrderClause>): boolean {
  return ContentSortOptions.supportsAscAndDesc(orderClause.field);
}

/** The sortKey/sortAscending pair <DataGrid> consumes to emit a per-column aria-sort. */
export type DataGridAriaSortProps = {
  sortKey: string | undefined;
  sortAscending: boolean;
};

/**
 * Derives the sortKey/sortAscending pair <DataGrid> needs to emit a correct per-column aria-sort
 * (W-21956346, WCAG 4.1.2). Called by AddUsersToGroupGrid, GroupsGrid, and ListView.
 *
 * It reads the same getPrimaryOrderClause accessor SortableHeader's arrow icon uses AND applies the
 * same doesOrderClauseSupportDirections gate, so the announced state tracks the drawn arrow. A
 * direction-less sort key (contentTypeOrder/targetType) draws no arrow, so it is reported as unsorted
 * (sortKey undefined -> aria-sort="none") rather than announcing a direction that isn't shown.
 * sortAscending is a real boolean because DataGrid coerces `sortAscending !== false`; an undefined
 * ascending on a sorted column would otherwise announce "ascending" over a descending arrow, so the
 * `=== true` normalization pins it to the drawn direction.
 *
 * aria-sort and the drawn arrow agree for a column matched by strict dataKey equality (both Groups
 * grids). ListView can also draw its arrow via the crass alias map (dataKeyToCrassFieldMap), which
 * DataGrid's strict sortKey match does not mirror -- a pre-existing gap, out of scope here.
 */
export function getDataGridAriaSortProps(currentColumnSortOrder: IOrderClause | undefined): DataGridAriaSortProps {
  const primaryOrderClause = getPrimaryOrderClause(currentColumnSortOrder ? [currentColumnSortOrder] : []);
  if (!doesOrderClauseSupportDirections(primaryOrderClause)) {
    return { sortKey: undefined, sortAscending: false };
  }
  return { sortKey: primaryOrderClause.field, sortAscending: primaryOrderClause.ascending === true };
}

export module SortableHeader {
  /**
   * Produces a header formatter that handles displaying sorted state.
   */
  export function Formatter(currentSort: ReadonlyArray<IOrderClause>, changeSort: (field: string) => void, tooltip?: JSX.Element, styleOverrides?: React.CSSProperties, siteurls?: SiteUrls): React.Factory<any> {
    const primaryOrderClause = getPrimaryOrderClause(currentSort);

    return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps> {

      private hasReportedWidth: boolean = false;

      render() {
        const col = this.props.col;
        const isSortable = col.sortable !== false;
        const hCellStyles: React.CSSProperties = {
          ...CommonStyles.Layout.TextEllipsis,
          paddingRight: CommonStyles.Sizes.MediumSpace,
          textDecoration: isSortable && this.props.hovered ? 'underline' : 'none',
          color: this.isSorted ? applyColor(DesignTokens.color.font.default.default, TabStyles.Typography.Color.PrimaryDark) : applyColor(DesignTokens.color.font.default.secondary, TabStyles.Typography.Color.PrimaryLight),
          boxSizing: 'border-box',
          ...styleOverrides,
        };

        const supportsDirections = doesOrderClauseSupportDirections(primaryOrderClause);

        /* eslint-disable jsx-a11y/no-static-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        return (
          <div style={hCellStyles} onClick={this.handleClick} data-tb-test-id={`tb-list-view-header-${this.props.col.dataKey}`}>
            <ArrowIcon sorted={this.isSorted} ascending={this.ascending} supportsAscDesc={supportsDirections} />
            {col.headerText}
            {tooltip}
          </div>
        );
      }

      componentDidMount() {
        // getBoundingClientRect doesnt play nice with overflow: 'hidden' property.
        // so measure width of header and add a 6 pixel padding
        // to ensure we don't elide header column text on initial render
        if (!this.hasReportedWidth && this.props.reportWidthFn) {
          this.hasReportedWidth = true;
          this.props.reportWidthFn(this.props.col.name, measureWidth(this) + 6);
        }
      }

      private get isSorted(): boolean {
        return primaryOrderClause.field === this.props.col.dataKey
          || (dataKeyToCrassFieldMap[this.props.col.dataKey] && dataKeyToCrassFieldMap[this.props.col.dataKey] === primaryOrderClause.field);
      }

      private get ascending(): boolean {
        return primaryOrderClause.ascending;
      }

      private handleClick = () => {
        if(showDMAwareness() && siteurls){
          dqwPreviewDialog(siteurls);
        }
        else if (this.props.col.sortable) {
          changeSort(this.props.col.dataKey);
          this.sendTelemetryEvent();
        }
      };

      private sendTelemetryEvent() {
        // if the primaryOrderClause is for this column, then handleClick is reversing the order. If it
        // is not, it means the user first clicked the header and we are using the default direction value.
        const asc = (this.props.col.dataKey === primaryOrderClause.field)
          ? !primaryOrderClause.ascending
          : (this.props.col.direction === 'asc');

        const event = CreateExplorerEvent<ExplorerSortEvent>({
          type: TelemetryEvents.ExplorerSort,
          sortField: this.props.col.dataKey,
          ascending: asc,
          eventSource: 'column'
        });
        Telemetry.sendEvent(event);
      }
    });
  }

  export function ArrowIcon(props: { sorted: boolean, ascending: boolean, supportsAscDesc: boolean }) {
    // arrow takes up left padding of hcell
    const arrowWidth = CommonStyles.Sizes.MediumSpace;
    const arrowHeight = CommonStyles.Sizes.NormalIconSize;
    let backgroundUrl: string | undefined = undefined;

    if (props.sorted && props.supportsAscDesc) {
      backgroundUrl =
      imgUrl(props.ascending ? 'narrow_sort_arrow_ascending.svg' : 'narrow_sort_arrow_descending.svg');
    }
    const arrowStyles = {
      width: arrowWidth,
      height: arrowHeight,
      display: 'inline-block',
      verticalAlign: 'middle',
      backgroundPosition: 'center',
      backgroundSize: arrowWidth + 'px ' + arrowHeight + 'px',
      backgroundRepeat: 'no-repeat',
      backgroundImage: backgroundUrl
    };
    return <div style={arrowStyles} />;
  }

}
